import PERMISSIONS from '../../constants/permissions';
import { EffectivePermissions } from '../../models/auth';
import { CommonPermissions, PermissionGroup, UserPermissions } from '../../models/permissions';
import { AccessRole } from '../../models/user-account';

type PermissionKey = keyof UserPermissions;
type ActionKey = keyof CommonPermissions;

const ALL = 'ALL';
const SUPER_ADMIN_ROLE = 'SA';

const { ACTIONS, ...GROUP_NAMES } = PERMISSIONS;
const GROUPS = Object.keys(GROUP_NAMES) as PermissionKey[];
const EVERY_ACTION = Object.keys(ACTIONS) as ActionKey[];

// sg_hospital_org RBAC_OPERATIONS codes. The backend has no Export operation.
const OPERATION_ACTIONS: Readonly<Record<string, ActionKey>> = {
  LS: 'List',
  VW: 'View',
  ADD: 'Add',
  EDT: 'Edit',
  DEL: 'Delete',
  SCH: 'Search',
  INV: 'Invite',
  ASG: 'Assign',
};

function isPermissionKey(object: string): object is PermissionKey {
  return (GROUPS as string[]).includes(object);
}

function actionsFor(operation: string): readonly ActionKey[] {
  if (operation === ALL) {
    return EVERY_ACTION;
  }
  const action = OPERATION_ACTIONS[operation];
  return action ? [action] : [];
}

function grant(group: PermissionGroup | undefined, actions: readonly ActionKey[]): PermissionGroup {
  const granted: PermissionGroup = { ...group };
  actions.forEach(action => (granted[action] = true));
  return granted;
}

// Same rules as the backend: its JWT super-user flag, and PermissionsGuard's per-object `ALL`.
export function toUserPermissions(
  effective: EffectivePermissions,
  roles: readonly AccessRole[] = []
): UserPermissions {
  const superUser =
    effective.isSuperUser ||
    roles.some(role => role.isSuperUser || role.extRef === SUPER_ADMIN_ROLE);

  const matrix: UserPermissions = {};
  if (superUser) {
    GROUPS.forEach(group => (matrix[group] = grant(undefined, EVERY_ACTION)));
    return matrix;
  }

  for (const { object, operation } of effective.permissions) {
    const actions = actionsFor(operation);
    if (isPermissionKey(object) && actions.length > 0) {
      matrix[object] = grant(matrix[object], actions);
    }
  }
  return matrix;
}
